// Drill-down scorecard. Reads top to bottom: a Bottom line (run it or not, and
// what to tell the client), the gates that decide the paths, then what we saw
// in the history in plain language. Every line says what it means and what to
// do; the numbers sit one click down in "Why we flag this", so passing checks
// keep the same receipts as flagged ones. All copy lives in data-qa-copy.
import './ScoreCard.scss';
import { DataQaGroup, DataQaThresholds, OutcomeEligibility } from '../../dataQaContract';
import { formatOutcomeLabel } from '../../forecasting-utils';
import {
  bottomLine,
  checkLines,
  CheckLine,
  isScalingOnlyChannel,
  LineGroup
} from '../data-qa-copy';

interface GateLine {
  pass: boolean;
  label: string;
  /** What to do, shown only when the gate fails. */
  next?: string;
}

// A path can pass overall while one outcome has too little history to run;
// always a sample-size shortfall, so the copy can name it. Call it out here.
const outcomeGate = (kpi: string, label: string, s: OutcomeEligibility): GateLine[] => {
  if (!s.eligible || (s.eligible_volume && s.eligible_revenue)) {
    return [];
  }
  const missing = formatOutcomeLabel(kpi, s.eligible_volume ? 'spend_revenue' : 'spend_volume');
  return [
    {
      pass: false,
      label: `${label}: ${missing} doesn't have enough history to run`,
      next: `Run the other outcome, or check back once ${missing} has more history.`
    }
  ];
};

const buildGates = (g: DataQaGroup, t: DataQaThresholds): GateLine[] => {
  const days = g.detail.sample_size.n_valid_days.spend;
  const weeks = g.detail.sample_size.n_valid_weeks.spend;
  const dark = g.detail.dark_days;
  const scalingGate: GateLine = {
    pass: g.detail.sample_size.scaling_pass,
    label: `Scaling: ${weeks ?? '–'} full weeks of history (needs ${t.scaling_min_weeks})`,
    next: `Use the forecast if it's offered, or check back once there are ${t.scaling_min_weeks} complete weeks with both spend and results.`
  };
  // Linear TV: forecasting isn't offered whatever the data says, so the forecast
  // data gates (and their "more history" / "try the rollup" advice) don't apply.
  if (isScalingOnlyChannel(g)) {
    return [
      {
        pass: false,
        label: 'Forecast: not available for linear TV yet',
        next: 'Use a Scaling analysis for budget questions.'
      },
      ...outcomeGate(g.kpi, 'Scaling', g.status.scaling),
      {
        ...scalingGate,
        next: 'Not enough complete weeks with both spend and results for Scaling yet; check back later.'
      }
    ];
  }
  return [
    ...outcomeGate(g.kpi, 'Forecast', g.status.forecast),
    ...outcomeGate(g.kpi, 'Scaling', g.status.scaling),
    {
      pass: g.detail.sample_size.forecast_pass,
      label: `Forecast: ${days ?? '–'} days of history (needs ${t.forecast_min_days})`,
      next: "Not enough history to forecast. Use Scaling if it's offered below, or check back once there's a full year."
    },
    {
      pass: dark.forecast_pass,
      label: `Forecast: ${dark.total} ${dark.total === 1 ? 'day' : 'days'} with no spend (longest run ${dark.max_run})`,
      next: 'Too many days with no spend. Try the rollup below, or Scaling. If the channel was live on those days, spend may be missing from the data: flag it to econ.'
    },
    scalingGate
  ];
};

const GateItem = ({ line }: { line: GateLine }) => (
  <li className={line.pass ? 'dq-line-pass' : 'dq-line-fail'}>
    <span className='dq-mark'>{line.pass ? '✓' : '✗'}</span>
    {line.label}
    {!line.pass && line.next && <div className='dq-next'>What to do: {line.next}</div>}
  </li>
);

const LINE_MARK: Record<LineGroup, string> = { before: '⚠', note: 'ℹ', fine: '✓' };
const LINE_CLASS: Record<LineGroup, string> = {
  before: 'dq-line-warn',
  note: 'dq-line-note',
  fine: 'dq-line-ok'
};

const CheckItem = ({ line }: { line: CheckLine }) => (
  <li className={LINE_CLASS[line.group]}>
    <span className='dq-mark'>{LINE_MARK[line.group]}</span>
    {line.verdict && <span className='dq-verdict'>{line.verdict}:</span>}{' '}
    <span className='dq-sig-name'>{line.title}</span>
    {line.body && <div className='dq-body'>{line.body}</div>}
    {line.next && <div className='dq-next'>What to do: {line.next}</div>}
    {line.why && (
      <details className='dq-why'>
        <summary>Why we flag this</summary>
        <span className='dq-receipts'>{line.why}</span>
      </details>
    )}
  </li>
);

const CheckGroup = ({ title, items }: { title: string; items: CheckLine[] }) => {
  if (items.length === 0) {
    return undefined;
  }
  return (
    <>
      <div className='dq-subgroup'>{title}</div>
      <ul className='dq-scorecard'>
        {items.map(line => (
          <CheckItem key={line.key} line={line} />
        ))}
      </ul>
    </>
  );
};

export const ScoreCard = ({ g, thresholds }: { g: DataQaGroup; thresholds: DataQaThresholds }) => {
  const gates = buildGates(g, thresholds);
  const orderedGates = [...gates.filter(line => !line.pass), ...gates.filter(line => line.pass)];
  const lines = checkLines(g, thresholds);
  return (
    <>
      <p className='dq-bottom-line' data-testid='dq-bottom-line'>
        {bottomLine(g)}
      </p>
      <h4>Can we forecast it?</h4>
      <ul className='dq-scorecard'>
        {orderedGates.map(line => (
          <GateItem key={line.label} line={line} />
        ))}
      </ul>
      <h4>What we saw in the history</h4>
      <p className='dq-grain-note'>These checks are for this exact slice.</p>
      {lines.length === 0 ? (
        <p className='dq-grain-note'>Not enough data to run these checks.</p>
      ) : (
        <>
          <CheckGroup title='Before you share' items={lines.filter(l => l.group === 'before')} />
          <CheckGroup title='Good to know' items={lines.filter(l => l.group === 'note')} />
          <CheckGroup title='Looks fine' items={lines.filter(l => l.group === 'fine')} />
        </>
      )}
    </>
  );
};
